import {useCallback, useMemo, useState} from 'react';
import {useQuery} from '@apollo/client';
import {Table, Input, Checkbox, Tag, Tooltip, Alert, Button} from 'antd';
import {SearchOutlined} from '@ant-design/icons';
import {useNavigate} from 'react-router-dom';
import moment from 'moment';
import {debounce} from 'utils/helper';
import {GET_INSTITUTIONAL_ACCOUNTS} from './resolvers';
import {KYB_STATUS_COLORS} from './kybConstants';
import {
  buildInstitutionalAccountFilter,
  formatEnumLabel,
  personName,
  unwrap,
} from './kybUtils';
import styles from './kybDashboard.module.scss';

/**
 * The verification queue.
 *
 * Sort is fixed server-side (`createdAt` descending) and there is no sort
 * parameter, so the table headers are deliberately not sortable — an
 * unsortable column that looks sortable is worse than a plain one.
 */
export const KybQueueTab = ({filters, onFiltersChange}) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchInput, setSearchInput] = useState(filters.search || '');

  const {data, loading, error} = useQuery(GET_INSTITUTIONAL_ACCOUNTS, {
    variables: {
      filter: buildInstitutionalAccountFilter({...filters, page, limit}),
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  });

  const {data: accounts, error: responseError} = unwrap(
    data?.getInstitutionalAccounts,
  );
  const pagination = data?.getInstitutionalAccounts?.pagination;

  // Any filter change invalidates the page you were on: staying on page 4 of a
  // result set that now has one page shows an empty table for no reason.
  const applyFilter = useCallback(
    patch => {
      setPage(1);
      onFiltersChange(patch);
    },
    [onFiltersChange],
  );

  const debouncedSearch = useMemo(
    () => debounce(value => applyFilter({search: value})),
    [applyFilter],
  );

  const columns = [
    {
      title: 'Business',
      key: 'entityName',
      render: (_, record) => (
        <div className={styles.entityCell}>
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => navigate(`/dashboard/kyb-dashboard/${record.id}`)}>
            {record.entityName || 'Unnamed business'}
          </button>
          <span className={styles.subtle}>
            {record.businessEmail || 'No business email'}
          </span>
        </div>
      ),
    },
    {
      title: 'Registration',
      key: 'registrationNumber',
      render: (_, record) => (
        <div className={styles.entityCell}>
          <span>{record.registrationNumber || '—'}</span>
          {record.duplicateRegistrationFlag && (
            <Tooltip title="Another account is registered with this number. It is a flag for a human to look at, not a block — approving is still your call.">
              <Tag color="gold">Duplicate registration</Tag>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: 'Country',
      dataIndex: 'countryOfIncorporation',
      key: 'countryOfIncorporation',
      render: value => value || '—',
    },
    {
      title: 'Primary contact',
      key: 'primaryContact',
      render: (_, record) => (
        <div className={styles.entityCell}>
          <span>{personName(record.primaryContact)}</span>
          <span className={styles.subtle}>
            {record.primaryContact?.email || '—'}
          </span>
        </div>
      ),
    },
    {
      title: 'Verification',
      key: 'kybStatus',
      render: (_, record) => (
        <Tag color={KYB_STATUS_COLORS[record.kybStatus] || 'default'}>
          {formatEnumLabel(record.kybStatus)}
        </Tag>
      ),
    },
    {
      // Approval and activation are two independent legs, so they are two
      // columns. Collapsing them hides which one a reviewer still owns.
      title: 'Account',
      key: 'isFullyActivated',
      render: (_, record) =>
        record.isFullyActivated ? (
          <Tag color="green">Live</Tag>
        ) : (
          <Tooltip
            title={
              record.kybStatus === 'APPROVED'
                ? "KYB is approved. Waiting on the administrator's personal KYC."
                : 'Not verified yet.'
            }>
            <Tag color="default">{formatEnumLabel(record.status)}</Tag>
          </Tooltip>
        ),
    },
    {
      title: 'Submitted',
      key: 'kybSubmittedAt',
      render: (_, record) =>
        record.kybSubmittedAt
          ? moment(record.kybSubmittedAt).format('DD MMM YYYY, HH:mm')
          : '—',
    },
    {
      title: '',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="link"
          className={styles.linkAction}
          onClick={() => navigate(`/dashboard/kyb-dashboard/${record.id}`)}>
          Open
        </Button>
      ),
    },
  ];

  const message = responseError || (error ? 'Could not load the queue.' : null);

  return (
    <div>
      <div className={styles.filterBar}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search entity name, registration number or business email"
          className={styles.searchInput}
          value={searchInput}
          onChange={event => {
            setSearchInput(event.target.value);
            debouncedSearch(event.target.value);
          }}
        />
        <Input
          allowClear
          placeholder="Country code, e.g. CI"
          className={styles.countryInput}
          value={filters.country}
          onChange={event => applyFilter({country: event.target.value})}
        />
        <Checkbox
          checked={filters.duplicatesOnly}
          onChange={event =>
            applyFilter({duplicatesOnly: event.target.checked})
          }>
          Duplicate registrations only
        </Checkbox>
      </div>

      {message && (
        <Alert
          type="error"
          showIcon
          message={message}
          className={styles.alert}
        />
      )}

      <Table
        rowKey="id"
        columns={columns}
        dataSource={accounts || []}
        loading={loading}
        // Scrolls inside the page when a narrow screen cannot fit the columns,
        // instead of spilling past the white area.
        scroll={{x: true}}
        locale={{
          emptyText: message
            ? 'Could not load accounts.'
            : 'No business accounts match these filters.',
        }}
        pagination={{
          current: pagination?.page || page,
          pageSize: pagination?.limit || limit,
          total: pagination?.total || 0,
          showSizeChanger: true,
          showTotal: total => `${total} account${total === 1 ? '' : 's'}`,
          onChange: (nextPage, nextSize) => {
            setPage(nextPage);
            setLimit(nextSize);
          },
        }}
      />
    </div>
  );
};
